/**
 * Yon menyudagi qo'shimcha modullarning sahifalari: yangiliklarning to'liq ro'yxati,
 * «Elektron yashil olam» savdo sahifasi, hali ishga tushmagan modullar («Tez kunda»)
 * hamda foydalanish shartlari va maxfiylik siyosati.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { moduleStatus } from "@/shared/data/publicNav";
import { useI18n } from "@/shared/i18n";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { useFavorites } from "@/shared/lib/shopStore";
import { Icon } from "@/shared/ui/Icon";
import type { IconName } from "@/shared/ui/Icon";

import { SplitHead } from "./landing/SplitHead";
import { MarketCard, useBuy, useMarketCatalog, useMarketServices } from "./landing/market";
import { MarketBanners } from "./landing/MarketBanners";
import {
  CartView,
  EMPTY_FILTERS,
  MarketCategories,
  MarketFilters,
  MarketMenu,
  ServicesView,
  applyFilters,
} from "./landing/MarketPanels";
import type { MarketFilterState, MarketSection } from "./landing/MarketPanels";
import { SectionPage } from "./SectionPages";

/* ------------------------------------------------------------------ yangiliklar */

/* ------------------------------------------------------------ elektron yashil olam */

/** Har bosishda ko'rsatiladigan qatorlar soni; kartochkalar soni = ustunlar × qatorlar, shunda oxirgi qator to'liq bo'ladi. */
const PAGE_ROWS = 3;

export function MarketPage() {
  const { t, locale } = useI18n();
  const catalog = useMarketCatalog(200);
  const buy = useBuy();
  const favorites = useFavorites();
  const [params, setParams] = useSearchParams();
  const services = useMarketServices();
  const section = (["promo", "favorites", "cart", "services"].includes(params.get("bolim") ?? "")
    ? params.get("bolim")
    : "products") as MarketSection;
  const category = params.get("toifa") ?? "";
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"new" | "cheap" | "dear" | "name">("new");
  const [filters, setFilters] = useState<MarketFilterState>(EMPTY_FILTERS);
  const [pages, setPages] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(4);
  const shown = pages * cols * PAGE_ROWS;

  const pickCategory = (nextCategory: string) => {
    const next = new URLSearchParams(params);
    next.delete("bolim");
    next.delete("tur");
    if (nextCategory) next.set("toifa", nextCategory);
    else next.delete("toifa");
    setParams(next, { replace: true });
    setPages(1);
  };
  const showServices = () => {
    const next = new URLSearchParams();
    next.set("bolim", "services");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const measure = () => {
      const n = getComputedStyle(el).gridTemplateColumns.split(" ").filter(Boolean).length;
      if (n > 0) setCols(n);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [catalog.items.length, section, category, query, filters]);

  const rows = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase(locale);
    const base = catalog.items.filter((item) => {
      if (section === "promo") return item.oldPrice > 0;
      if (section === "favorites") return favorites.has(item.code);
      return true;
    });
    const filtered = base.filter(
      (item) =>
        (section === "favorites" || section === "promo" || !category || item.categoryCode === category) &&
        applyFilters(item, filters) &&
        (!needle || `${item.name} ${item.seller} ${item.region}`.toLocaleLowerCase(locale).includes(needle)),
    );
    if (sort === "cheap") return [...filtered].sort((a, b) => a.price - b.price);
    if (sort === "dear") return [...filtered].sort((a, b) => b.price - a.price);
    if (sort === "name") return [...filtered].sort((a, b) => a.name.localeCompare(b.name, "uz"));
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog.items, section, category, query, sort, filters, locale, favorites.codes]);

  const real = catalog.items.filter((item) => !item.sample);
  const title =
    section === "promo"
      ? "Aksiyalar"
      : section === "favorites"
        ? "Sevimlilar"
        : section === "cart"
          ? "Savat"
          : section === "services"
            ? "Xizmatlar"
            : t("market.all");
  const emptyText =
    section === "promo"
      ? "Hozircha aksiyadagi mahsulotlar yo'q."
      : section === "favorites"
        ? "Sevimlilar ro'yxati bo'sh. Mahsulot kartochkasidagi yurakcha tugmasini bosing."
        : t("market.empty");

  return (
    <SectionPage revealKey={`${catalog.items.length}-${section}-${category}-${sort}-${shown}`}>
      <section className="section section-alt" id="market-all" aria-labelledby="market-all-title">
        <div className="container">
          <h2 id="market-all-title" className="visually-hidden">
            {title}
          </h2>

          <div className="market-layout">
            <aside className="market-side" aria-label="Menyu va filtrlar">
              <MarketMenu active={section} />
              {section === "cart" ? null : (
                <>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm market-panel-toggle"
                    aria-expanded={panelOpen}
                    onClick={() => setPanelOpen((open) => !open)}
                  >
                    <Icon name="filter" size={16} />
                    Kategoriya va filtrlar
                  </button>
                  <div className={`market-panels${panelOpen ? " is-open" : ""}`}>
                    <MarketCategories
                      items={real}
                      category={category}
                      servicesCount={services.items.length}
                      servicesActive={section === "services"}
                      onPick={pickCategory}
                      onServices={showServices}
                    />
                    {section === "services" ? null : (
                      <MarketFilters
                        items={real}
                        value={filters}
                        onChange={(next) => {
                          setFilters(next);
                          setPages(1);
                        }}
                      />
                    )}
                  </div>
                </>
              )}
            </aside>

            <div className="market-main">
              <MarketBanners kind="market" />
              {section === "cart" ? (
                <CartView items={real} />
              ) : section === "services" ? (
                <>
                  <div className="market-toolbar" data-reveal>
                    <label className="market-search">
                      <Icon name="search" size={16} />
                      <span className="visually-hidden">{t("market.search")}</span>
                      <input
                        className="input"
                        type="search"
                        value={query}
                        placeholder="Xizmat nomi bo'yicha qidirish…"
                        onChange={(event) => setQuery(event.target.value)}
                      />
                    </label>
                    <span className="market-found">Xizmatlar: {services.items.length}</span>
                  </div>
                  {services.loading ? (
                    <p className="market-empty">{t("state.loading")}</p>
                  ) : (
                    <ServicesView services={services.items} query={query} />
                  )}
                </>
              ) : (
                <>
                  <div className="market-toolbar" data-reveal>
                    <label className="market-search">
                      <Icon name="search" size={16} />
                      <span className="visually-hidden">{t("market.search")}</span>
                      <input
                        className="input"
                        type="search"
                        value={query}
                        placeholder={t("market.search")}
                        onChange={(event) => {
                          setQuery(event.target.value);
                          setPages(1);
                        }}
                      />
                    </label>
                    <label className="market-sort">
                      <span className="visually-hidden">Saralash</span>
                      <select
                        className="input"
                        value={sort}
                        onChange={(event) => setSort(event.target.value as typeof sort)}
                      >
                        <option value="new">Yangilari avval</option>
                        <option value="cheap">Narx: arzonidan</option>
                        <option value="dear">Narx: qimmatidan</option>
                        <option value="name">Nomi bo'yicha</option>
                      </select>
                    </label>
                    <span className="market-found">Topildi: {rows.length}</span>
                  </div>

                  {catalog.sample ? (
                    <p className="sample-note">
                      <Icon name="info" size={14} />
                      {t("market.sampleNote")}
                    </p>
                  ) : null}

                  {rows.length ? (
                    <div className="market-grid" ref={gridRef}>
                      {rows.slice(0, shown).map((item, index) => (
                        <MarketCard key={item.key} item={item} index={index} onBuy={buy} />
                      ))}
                    </div>
                  ) : (
                    <p className="market-empty">{catalog.loading ? t("state.loading") : emptyText}</p>
                  )}

                  {rows.length > shown ? (
                    <div className="market-more">
                      <button type="button" className="btn btn-outline" onClick={() => setPages((n) => n + 1)}>
                        {t("action.more")} ({rows.length - shown})
                      </button>
                    </div>
                  ) : null}
                </>
              )}

              <p className="market-hint">
                <Icon name="lock" size={14} />
                Ko'rish, sevimlilar va savat uchun tizimga kirish shart emas; buyurtmani rasmiylashtirishda OneID orqali kirasiz.
              </p>
            </div>
          </div>
        </div>
      </section>
    </SectionPage>
  );
}

/* ---------------------------------------------------------------- tez kunda */

/**
 * Hali ishga tushmagan modul: nomi, qisqa tavsifi va hozir ishlab turgan xizmatlarga
 * havolalar. Modul tayyor bo'lganda shu sahifa uning haqiqiy sahifasi bilan
 * almashtiriladi va `MODULE_STATUS` dagi yozuv olib tashlanadi.
 */
function ComingSoon({
  module,
  scene,
  icon,
  text,
}: {
  module: string;
  scene: string;
  icon: IconName;
  text: string;
}) {
  const { t } = useI18n();
  const soon = moduleStatus(module) === "soon";
  const links: Array<{ to: string; icon: IconName; label: string }> = [
    { to: SECTION_PATHS.services, icon: "briefcase", label: t("nav.services") },
    { to: SECTION_PATHS.appeal, icon: "send", label: t("nav.appealSend") },
    { to: SECTION_PATHS.map, icon: "map", label: t("nav.map") },
    { to: SECTION_PATHS.market, icon: "store", label: t("nav.market") },
  ];

  return (
    <SectionPage>
      <section className="section" aria-labelledby={`soon-${module}`}>
        <div className="container">
          <SplitHead
            scene={scene}
            eyebrow={
              soon ? (
                <>
                  {t("nav.soon")} <Icon name="clock" size={13} />
                </>
              ) : undefined
            }
            title={t(`nav.${module}`)}
            titleId={`soon-${module}`}
            lead={t(text)}
          />

          <div className="soon-card" data-reveal>
            <span className="soon-icon">
              <Icon name={icon} size={30} />
            </span>
            <div>
              <h3>{t("soon.title")}</h3>
              <p>{t("soon.text")}</p>
            </div>
          </div>

          <h3 className="soon-sub" data-reveal>
            {t("soon.meanwhile")}
          </h3>
          <ul className="soon-links">
            {links.map((link) => (
              <li key={link.to} data-reveal>
                <Link className="soon-link" to={link.to}>
                  <Icon name={link.icon} size={18} />
                  {link.label}
                  <Icon name="arrowRight" size={16} className="soon-go" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </SectionPage>
  );
}

export function LeasePage() {
  return <ComingSoon module="lease" scene="map" icon="polygon" text="quick.lease" />;
}

export function SeedlingsPage() {
  return <ComingSoon module="seedlings" scene="guides" icon="leaf" text="quick.seedlings" />;
}

/* --------------------------------------------------------------- huquqiy sahifalar */

interface LegalClause {
  title: string;
  text: string;
}

/**
 * Foydalanish shartlari va maxfiylik siyosati matnlari — loyiha varianti:
 * e'lon qilishdan oldin yuridik xizmat tomonidan ko'rib chiqilishi kerak.
 */
const TERMS: LegalClause[] = [
  {
    title: "1. Umumiy qoidalar",
    text: "Smart Forestry — o'rmonlar, yashil hududlar va tabiiy resurslar haqidagi ma'lumotlar, interaktiv xarita, elektron xizmatlar hamda o'rmon mahsulotlari savdosini birlashtiruvchi milliy axborot platformasi. Platformadan foydalanish ushbu shartlarni qabul qilishni anglatadi.",
  },
  {
    title: "2. Ochiq ma'lumotlar",
    text: "Yangiliklar, ochiq xarita, statistika va mahsulotlar katalogi ro'yxatdan o'tmasdan ko'riladi. Ma'lumotlardan foydalanganda manba (Smart Forestry) ko'rsatilishi shart.",
  },
  {
    title: "3. Elektron xizmatlar va kirish",
    text: "Ariza, shartnoma, to'lov va xarid kabi amallar OneID orqali tasdiqlangan shaxs nomidan bajariladi. Foydalanuvchi o'z hisobi orqali bajarilgan harakatlar uchun javobgar bo'ladi va kirish ma'lumotlarini uchinchi shaxslarga bermaydi.",
  },
  {
    title: "4. Murojaatlar",
    text: "Murojaatda haqqoniy ma'lumot ko'rsatilishi kerak. Tuhmat, haqorat va qonunga zid kontent o'z ichiga olgan murojaatlar ko'rib chiqilmasligi mumkin.",
  },
  {
    title: "5. Elektron yashil olam savdosi",
    text: "Mahsulot narxi, miqdori va yetkazib berish shartlari sotuvchi tomonidan belgilanadi. Buyurtma kabinet orqali rasmiylashtiriladi va uning holati shu yerda kuzatiladi.",
  },
  {
    title: "6. Mas'uliyat cheklovi",
    text: "Platforma texnik ishlar uchun vaqtincha to'xtatilishi mumkin; bu haqda bosh sahifada xabar beriladi. Agentlik ma'lumotlarning dolzarbligini ta'minlashga harakat qiladi, biroq ularning to'liq xatosizligiga kafolat bermaydi.",
  },
  {
    title: "7. O'zgartirishlar",
    text: "Shartlar yangilanishi mumkin. Yangi tahrir shu sahifada e'lon qilingan paytdan kuchga kiradi.",
  },
];

const PRIVACY: LegalClause[] = [
  {
    title: "1. Qanday ma'lumotlar yig'iladi",
    text: "OneID orqali kirishda — ism-familiya va identifikatsiya ma'lumotlari; murojaatda — kiritilgan matn, joylashuv, ilova qilingan foto/video va aloqa telefoni; baholashda — yulduz va izoh (anonim).",
  },
  {
    title: "2. Ma'lumotlardan foydalanish maqsadi",
    text: "Ma'lumotlar xizmat ko'rsatish, murojaatlarni ko'rib chiqish va ijrosini nazorat qilish, buyurtmalarni rasmiylashtirish hamda platforma sifatini yaxshilash uchun ishlatiladi.",
  },
  {
    title: "3. Saqlash va himoya",
    text: "Shaxsiy ma'lumotlar O'zbekiston Respublikasi hududidagi serverlarda, shifrlangan holda saqlanadi. Kirish huquqi foydalanuvchi roliga qarab cheklanadi va jurnalga yoziladi.",
  },
  {
    title: "4. Uchinchi shaxslarga berish",
    text: "Ma'lumotlar qonunda belgilangan hollarda va xizmat ko'rsatish uchun zarur bo'lgan davlat tizimlariga (masalan, OneID, E-auksion) uzatiladi. Boshqa maqsadlarda berilmaydi.",
  },
  {
    title: "5. Baholash va statistika",
    text: "Tizimni baholashda IP manzil saqlanmaydi: takroriy ovozni cheklash uchun faqat qaytarib bo'lmaydigan xesh ishlatiladi. Umumiy baho va baholar soni ommaviy ko'rsatiladi, izohlar esa faqat kontent-menejerga ochiq.",
  },
  {
    title: "6. Foydalanuvchi huquqlari",
    text: "Foydalanuvchi o'z ma'lumotlari haqida so'rash, ularni tuzatish yoki o'chirishni talab qilish huquqiga ega. Buning uchun «Aloqa» sahifasidagi kanallar orqali murojaat qiling.",
  },
];

function LegalPage({
  scene,
  title,
  lead,
  clauses,
}: {
  scene: string;
  title: ReactNode;
  lead: string;
  clauses: LegalClause[];
}) {
  return (
    <SectionPage>
      <section className="section" aria-labelledby="legal-title">
        <div className="container container-narrow">
          <SplitHead scene={scene} title={title} titleId="legal-title" lead={lead} />
          <div className="legal" data-reveal>
            {clauses.map((clause) => (
              <section key={clause.title}>
                <h3>{clause.title}</h3>
                <p>{clause.text}</p>
              </section>
            ))}
          </div>
        </div>
      </section>
    </SectionPage>
  );
}

export function TermsPage() {
  const { t } = useI18n();
  return (
    <LegalPage
      scene="about"
      title={t("footer.terms")}
      lead="Platformadan foydalanish tartibi va tomonlarning mas'uliyati."
      clauses={TERMS}
    />
  );
}

export function PrivacyPage() {
  const { t } = useI18n();
  return (
    <LegalPage
      scene="faq"
      title={t("footer.privacy")}
      lead="Shaxsiy ma'lumotlar qanday yig'ilishi, saqlanishi va himoya qilinishi."
      clauses={PRIVACY}
    />
  );
}
